import React from 'react';
import styled, { css } from 'styled-components';

// Componentes de layout
export const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;
`;

export const Header = styled.div`
  display: flex;
  justify-content: between;
  align-items: center;
  margin-bottom: 3rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #ecf0f1;
`;

export const Title = styled.h1`
  color: #2c3e50;
  font-size: 2.5rem;
  margin: 0;
`;

// Componentes de tabla
export const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  
  /* Scrollbar personalizado para tablas */
  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  &::-webkit-scrollbar-thumb {
    background: #FDBF37;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #e8891a;
  }
`;

export const Table = styled.table`
  width: 100%;
  min-width: 600px; /* Ancho mínimo para evitar que se comprima demasiado */
  border-collapse: collapse;
  background: white;
  border-radius: 12px;
  overflow: hidden;
`;

export const TableHeader = styled.th`
  background: #f8f9fa;
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: #2c3e50;
  border-bottom: 2px solid #ecf0f1;
`;

export const TableCell = styled.td`
  padding: 1rem;
  border-bottom: 1px solid #ecf0f1;
  color: #2c3e50;
`;

// Componentes de botones
export const ActionButton = styled.button`
  background: linear-gradient(135deg, #3498db, #2980b9);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  margin-bottom: 2rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(52, 152, 219, 0.3);
  }
`;

export const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

export const IconButton = styled.button<{ variant?: 'edit' | 'delete' }>`
  background: ${props => {
    switch (props.variant) {
      case 'edit': return '#FDBF37';
      case 'delete': return '#e74c3c';
      default: return '#3498db';
    }
  }};
  color: white;
  border: none;
  padding: 0.5rem;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    opacity: 0.8;
    transform: scale(1.05);
  }
`;

export const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  background: ${props => props.variant === 'secondary' ? '#95a5a6' : '#3498db'};
  color: white;

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }
`;

export const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 1rem;
`;

// Componentes de modal
export const Modal = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'show'
})<{ show: boolean }>`
  display: ${props => props.show ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

export const ModalContent = styled.div`
  position: relative;
  background: white;
  padding: 2rem;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  overflow-y: auto;
`;

// Componentes de formulario
export const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

export const Label = styled.label`
  font-weight: 600;
  color: #2c3e50;
`;

export const Input = styled.input`
  padding: 0.75rem;
  border: 2px solid #ecf0f1;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #3498db;
  }
`;

export const TextArea = styled.textarea`
  padding: 0.75rem;
  border: 2px solid #ecf0f1;
  border-radius: 8px;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #3498db;
  }
`;

export const Select = styled.select`
  padding: 0.75rem;
  border: 2px solid #ecf0f1;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  cursor: pointer;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #3498db;
  }
`;

// Componentes de pestañas
export const TabsContainer = styled.div`
  margin-bottom: 2rem;
`;

export const TabsList = styled.div`
  display: flex;
  gap: 0.5rem;
  border-bottom: 2px solid #ecf0f1;
`;

export const TabButton = styled.button<{ active?: boolean }>`
  padding: 1rem 2rem;
  border: none;
  background: ${props => props.active ? '#3498db' : 'transparent'};
  color: ${props => props.active ? 'white' : '#7f8c8d'};
  border-radius: 8px 8px 0 0;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.active ? '#3498db' : '#ecf0f1'};
  }
`;

export const Tab = styled.button<{ active: boolean }>`
  padding: 1rem 2rem;
  border: none;
  background: ${props => props.active ? '#3498db' : 'transparent'};
  color: ${props => props.active ? 'white' : '#7f8c8d'};
  border-radius: 8px 8px 0 0;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.active ? '#3498db' : '#ecf0f1'};
  }
`;

export const TabContent = styled.div`
  background: white;
  border-radius: 0 12px 12px 12px;
  padding: 2rem;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
`;

// Componentes de estadísticas
export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

export const StatCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
`;

export const StatIcon = styled.div<{ color?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  border-radius: 12px;
  background-color: ${props => props.color || '#3498db'};
  color: white;
  font-size: 1.5rem;
`;

export const StatInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

export const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  color: #2c3e50;
`;

export const StatLabel = styled.div`
  font-size: 0.9rem;
  color: #7f8c8d;
`;

// Componentes de paginación
export const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin-top: 30px;
  padding: 20px;
  
  @media (max-width: 768px) {
    gap: 8px;
    margin-top: 20px;
    padding: 15px;
  }
`;

export const PaginationButton = styled.button<{ active?: boolean; disabled?: boolean; variant?: 'nav' | 'page' }>`
  ${props => props.variant === 'nav' ? `
    padding: 10px 15px;
    background: ${props.disabled ? '#9E9E9E' : '#2A3A53'};
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: ${props.disabled ? 'not-allowed' : 'pointer'};
    transition: all 0.3s ease;
    box-shadow: ${props.disabled ? 'none' : '0 3px 10px rgba(42, 58, 83, 0.3)'};
    
    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 5px 15px rgba(42, 58, 83, 0.4);
      background: #1e2a3a;
    }
  ` : `
    width: 40px;
    height: 40px;
    background: ${props.active 
      ? 'linear-gradient(135deg, #FDBF37 0%, #FDBF37 100%)' 
      : 'white'};
    color: ${props.active ? 'white' : '#2c3e50'};
    border: ${props.active ? 'none' : '2px solid #e9ecef'};
    border-radius: 50%;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: ${props.active 
      ? '0 4px 12px rgba(255, 193, 7, 0.4)' 
      : '0 2px 6px rgba(0, 0, 0, 0.1)'};
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:hover:not(:disabled) {
      ${!props.active ? `
        background: #f8f9fa;
        transform: scale(1.1);
      ` : ''}
    }
  `}
  
  @media (max-width: 768px) {
    ${props => props.variant === 'nav' ? `
      padding: 8px 12px;
      font-size: 12px;
    ` : `
      width: 35px;
      height: 35px;
      font-size: 12px;
    `}
  }
`;

export const PaginationInfo = styled.div`
  color: #7f8c8d;
  font-size: 0.9rem;
  text-align: center;
  margin: 1rem 0;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
    margin: 0.5rem 0;
  }
`;

export const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  
  @media (max-width: 768px) {
    gap: 6px;
  }
`;

export const PaginationEllipsis = styled.span`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  font-size: 16px;
  font-weight: 600;
  
  @media (max-width: 768px) {
    width: 35px;
    height: 35px;
    font-size: 14px;
  }
`;

interface FloatingLabelInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  as?: 'input' | 'select';
  children?: React.ReactNode; // Para opciones de select
  style?: React.CSSProperties; // Permitir estilos inline
}

const Wrapper = styled.div`
  position: relative;
  margin-bottom: 22px;
  width: 100%;
`;

const sharedInputStyles = css`
  width: 100%;
  padding: 1.1rem 1rem 0.6rem 1rem;
  border: 1.5px solid #ddd;
  border-radius: 8px;
  font-size: 16px;
  background: #fff;
  outline: none;
  transition: border-color 0.2s;
  &:focus {
    border-color: #FDBF37;
  }
`;

// Floating label input
const FloatingInput = styled.input`
  ${sharedInputStyles}
  padding-top: 1.6rem;
`;

const FloatingSelect = styled.select`
  ${sharedInputStyles}
  padding-top: 1.6rem;
  appearance: none;
`;

const FloatingLabel = styled.label<{ floating: boolean }>`
  position: absolute;
  left: 1rem;
  top: ${({ floating }) => (floating ? '-0.7rem' : '1.35rem')};
  font-size: ${({ floating }) => (floating ? '0.92rem' : '1.05rem')};
  color: ${({ floating }) => (floating ? '#FDBF37' : '#aaa')};
  background: #fff;
  padding: 0 4px;
  pointer-events: none;
  transition: all 0.18s cubic-bezier(0.4,0,0.2,1);
  z-index: 3;
  line-height: 1.1;
`;

export const FloatingLabelInput: React.FC<FloatingLabelInputProps> = ({ label, as = 'input', children, style, ...props }) => {
  const [focused, setFocused] = React.useState(false);
  const value = props.value ?? '';
  const floating = focused || value !== '';

  return (
    <Wrapper style={style}>
      {as === 'select' ? (
        <FloatingSelect
          {...(props as React.SelectHTMLAttributes<HTMLSelectElement>)}
          onFocus={e => { setFocused(true); props.onFocus?.(e as any); }}
          onBlur={e => { setFocused(false); props.onBlur?.(e as any); }}
        >
          {children}
        </FloatingSelect>
      ) : (
        <FloatingInput
          {...props}
          onFocus={e => { setFocused(true); props.onFocus?.(e); }}
          onBlur={e => { setFocused(false); props.onBlur?.(e); }}
        />
      )}
      <FloatingLabel floating={floating}>{label}</FloatingLabel>
    </Wrapper>
  );
};